// MeetCallWidget — persistent floating call dock mounted ONCE at the shell
// root (packages/workspace/app.js — same pattern as OnlineMembersDock).
// Visible whenever a LiveKit Room is connected AND the user is NOT looking
// at the /meet call view itself, so navigating anywhere in the workspace
// keeps the call alive with a visible, actionable handle back to it.
//
// The LiveKit Room is a module variable in connectCall.js — navigation never
// tears it down. The ONLY disconnect paths are explicit user actions (this
// widget's Leave, ControlBar Leave/End) plus the beforeunload hook.
//
// Bottom-RIGHT corner: OnlineMembersDock owns bottom-left and the
// CanvasPromptTextarea owns bottom-center. On the canvas routes the shell's
// Zoombar shares this corner: the dock sits above it (`--zoombar-clear`, its
// top edge from the viewport bottom: a length the shell registers and
// measures, 0 elsewhere — this dock mounts only at the shell root).
//
// FLY IN A CALL (functions/meetFly.js): while the call's THEATER is open —
// the call's pages on the main layer over the call view — the theater's own
// bar carries the call (workspace AssistantFlyCallView: live dot, people,
// the live caption, mic, Back to call, Leave), so this dock steps aside.
// Off the call, a call with pages offers them here as well.
//
// LAYER: above the main layer (AssistantMainLayer, 1250 — the Fly canvas and
// the theater), below the sheets and popovers (1340+). At 1150 the main layer
// covered the dock's top edge and took the press meant for it: the minimized
// call "would not open back" (Nika 2026-10-23).

import { getLivekitRoom } from '../functions/connectCall.js'
import { routeTo } from '../functions/meetUtils.js'
import {
  meetFlyCallOf,
  meetFlyVisiblePages,
  meetFlyTheaterOpen
} from '../functions/meetFlyState.js'
import { stripWorkspacePrefix } from '@symbo-ls/workspace-shared/functions/workspacePrefix.js'

const _meetMe = (s) => (s.root?.livekitParticipants || []).find((p) => p.isLocal)

const _activeRoom = (s) => (s.root?.rooms || []).find((r) => r.id === s.root?.activeRoomId) || null

const _connectedRoomId = (s) => {
  const live = getLivekitRoom()
  return live?._smblsRoomId || s.root?.activeRoomId || null
}

export const MeetCallWidget = {
  // Show whenever a call is live and the user is off the call view. The
  // path check matters: meetView stays 'call' in root state after SPA
  // navigation, so gating on meetView alone kept the widget hidden on
  // every other page (the original "navigating kicks me out" symptom).
  hide: (el, s) => {
    if (!s.root?.livekitConnected) return true
    // The theater covers the call view and its bar carries the call.
    if (meetFlyTheaterOpen(s.root)) return true
    // The call view on screen in a pane (the meet sheet over another route):
    // the call is already there — two call surfaces read as two calls.
    const inSlot = [s.root?._canvasSlot, s.root?._canvasSlotSecond].some(
      (r) => typeof r === 'string' && stripWorkspacePrefix(r.split('?')[0]) === '/meet'
    )
    if (inSlot && s.root?.meetView === 'call') return true
    const path = stripWorkspacePrefix(
      s.root?.currentPath || (typeof window !== 'undefined' ? window.location.pathname : '/')
    )
    return path === '/meet' && s.root?.meetView === 'call'
  },
  // The dock sits at the shell root where nothing else re-renders it —
  // subscribe to the signals that change its visibility or content.
  subscribeTo: [
    'livekitConnected',
    'currentPath',
    'meetView',
    'livekitParticipants',
    'rooms',
    'meetFly',
    '_canvasSlot',
    '_canvasSlotSecond'
  ],

  position: 'fixed',
  bottom: 'calc(var(--spacing-A) + var(--zoombar-clear))',
  right: 'A',
  zIndex: '1300',
  flow: 'x',
  align: 'center left',
  gap: 'Z1',
  padding: 'Z Z1',
  // Floating dock over live content — the `float`-elevation glass island
  // class, per DESIGN_LANGUAGE.md §3's own radiusSheet definition
  // ("modals, sheets, docked panels, floating islands").
  round: 'radiusSheet',
  theme: 'canvas-card-glass',

  LiveDot: {
    tag: 'span',
    boxSize: 'X2',
    minWidth: 'X2',
    round: '100%',
    // the success ink pair (one green per scheme), never a raw hue — the
    // same dot as VideoRoom's LiveLine and the Fly call view's LiveDot
    background: 'successInk'
  },

  Info: {
    flow: 'y',
    gap: 'W',
    minWidth: '0',
    maxWidth: 'F2',

    RoomName: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'title',
      fontWeight: '700',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => el.call('polyglot', _activeRoom(s)?.name || 'meet.widget.inCall')
    },

    Caption: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      whiteSpace: 'nowrap',
      text: (el, s) => {
        const n = (s.root?.livekitParticipants || []).length
        const mic = el.call(
          'polyglot',
          _meetMe(s)?.isMicEnabled ? 'meet.widget.micOn' : 'meet.widget.micOff'
        )
        return el
          .call('polyglot', 'meet.widget.participantLine')
          .replace('{count}', n || 1)
          .replace('{mic}', mic)
      }
    }
  },

  Controls: {
    flow: 'x',
    align: 'center left',
    gap: 'Z',

    MicBtn: {
      extends: 'MeetControlBarIconButton',
      width: 'B2',
      height: 'B2',
      fontSize: 'Z1',
      // The bar's own OFF plate (ControlBar MicBtn): the danger ink on its
      // wash, so a muted mic reads at a glance in the dock too.
      theme: 'secondary',
      isOff: (el, s) => !_meetMe(s)?.isMicEnabled,
      '.isOff': {
        color: 'dangerInk',
        background: 'color-mix(in srgb, currentColor 12%, var(--color-tintBase))'
      },
      ariaPressed: (el, s) => (_meetMe(s)?.isMicEnabled ? 'true' : 'false'),
      title: (el) => el.call('polyglot', 'meet.widget.toggleMic'),
      Icon: {
        icon: (el, s) => (_meetMe(s)?.isMicEnabled ? 'mic' : 'micOff')
      },
      onClick: async (e, el, s) => {
        const room = getLivekitRoom()
        if (!room) return
        const enabled = room.localParticipant.isMicrophoneEnabled
        await room.localParticipant.setMicrophoneEnabled(!enabled)
      }
    },

    // The call's pages, from anywhere off them (another route, or the call
    // view with the theater closed is the control bar's own button).
    PagesBtn: {
      extends: 'MeetControlBarIconButton',
      width: 'B2',
      height: 'B2',
      fontSize: 'Z1',
      theme: 'secondary',
      hide: (el, s) =>
        meetFlyTheaterOpen(s.root) || !meetFlyVisiblePages(meetFlyCallOf(s.root)).length,
      title: (el) => el.call('polyglot', 'meet.widget.showPages'),
      ariaLabel: (el) => el.call('polyglot', 'meet.widget.showPages'),
      Icon: { name: 'lnPage' },
      onClick: (e, el, s) => {
        const id = _connectedRoomId(s)
        const call = meetFlyCallOf(s.root)
        if (!id || !call) return
        s.root?.update({ activeRoomId: id, meetView: 'call', meetFly: { ...call, open: true } })
        routeTo(el, '/meet')
        if (typeof window !== 'undefined') {
          history.replaceState(null, '', `#room=${id}&view=call`)
        }
      }
    },

    ReturnBtn: {
      extends: 'MeetControlBarIconButton',
      width: 'B2',
      height: 'B2',
      fontSize: 'Z1',
      theme: 'primary',
      title: (el, s) =>
        el.call(
          'polyglot',
          meetFlyTheaterOpen(s.root) ? 'meet.fly.backToCall' : 'meet.widget.returnToCall'
        ),
      Icon: { name: 'video' },
      onClick: (e, el, s) => {
        // The theater open over the call: the call comes back in place.
        if (meetFlyTheaterOpen(s.root)) {
          const call = meetFlyCallOf(s.root)
          if (call) s.root?.update({ meetFly: { ...call, open: false } })
          return
        }
        const id = _connectedRoomId(s)
        if (!id) return
        s.root?.update({ activeRoomId: id, meetView: 'call' })
        routeTo(el, '/meet')
        if (typeof window !== 'undefined') {
          history.replaceState(null, '', `#room=${id}&view=call`)
        }
      }
    },

    LeaveBtn: {
      extends: 'MeetControlBarIconButton',
      width: 'B2',
      height: 'B2',
      fontSize: 'Z1',
      theme: null,
      // A fixed brand-hue plate (no `theme:`), so the ink is fixed too — and it is
      // the brand `fillInk` (near-black), not white. White on a raw brand hue read
      // under the 4.5:1 floor in BOTH themes (blue 3.62, red 3.21, green 2.81);
      // near-black reads 5.66 / 6.39 / 7.28. Nika's decision 2026-09-21
      // (treatment 3, design-direction.md §6).
      background: 'red',
      color: 'fillInk',
      title: (el) => el.call('polyglot', 'meet.widget.leaveCall'),
      Icon: { name: 'close' },
      onClick: async (e, el, s) => {
        await el.call('disconnectCall')
        s.root?.update({ meetView: 'list' })
      }
    }
  }
}
